import { UserPlanVo, UserProfile } from "@/api";
import DiscordIcon from "@/assets/icons/brand/discord.svg";
import WechatIcon from "@/assets/icons/brand/wechat.svg";
import LogoutIcon from "@/assets/icons/logout.svg";
import ManageIcon from "@/assets/icons/ui/manage.svg";
import ProblemFeedbackIcon from "@/assets/icons/ui/problem-feedback.svg";
import creditIcon from "@/assets/images/credit-icon.png";
import defaultAvatar from "@/assets/images/default-avatar.jpg";
import LogoIcon from "@/assets/images/logo.svg";
import TextLogoIcon from "@/assets/images/text-logo.svg";
import { DISCORD_INVITE_URL } from "@/common/const";
import { Button } from "@/component/Button";
import { formatNumberWithCommas } from "@/util/formatNumber";
import { Avatar, Popover } from "antd";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React from "react";
import styles from "./index.module.css";

export interface HeaderProps {
  userLoading: boolean;
  user: UserProfile | null;
  userPlan: UserPlanVo | null;
  userCredits: number;
  onUpgradeClick: () => void;
  onManageSubscriptionClick: () => void;
}

const feedbackFormBaseUrl =
  "https://j0yswlgboxz.feishu.cn/share/base/form/shrcnHs6ytPFjKYMWQzj9QrFOxd?prefill_Project+Link=";

export const Header: React.FC<HeaderProps> = ({
  userLoading,
  user,
  userPlan,
  userCredits,
  onUpgradeClick,
  onManageSubscriptionClick,
}) => {
  const router = useRouter();
  const [feedbackUrl, setFeedbackUrl] = React.useState(feedbackFormBaseUrl);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const encodedUrl = encodeURIComponent(window.location.href);
    setFeedbackUrl(`${feedbackFormBaseUrl}${encodedUrl}`);
  }, []);

  // 预热 Pricing 拦截路由的 UI 资源，减少首跳等待
  React.useEffect(() => {
    router.prefetch("/pricing");
    void import("@/component/FullScreenModal");
  }, [router]);

  const creditPlanLink = !userLoading && (
    <Link href="/pricing" data-disable-progress={true}>
      <div className="flex items-center gap-2 px-4 py-2 font-medium rounded-full border border-text-primary/[.07] bg-black/20 backdrop-blur-xl text-text-primary cursor-pointer transition-all hover:bg-primary/[.15] hover:backdrop-blur-none">
        {user ? (
          <>
            <span className="flex items-center gap-2">
              <Image src={creditIcon} alt="Credit" width={14} height={14} />
              <span>{formatNumberWithCommas(userCredits)}</span>
            </span>
            <div className="w-px h-[15px] bg-white/60"></div>

            <span>
              {userPlan ? userPlan.memberShipName : user ? "" : "Free"}
            </span>
          </>
        ) : (
          <>Pricing</>
        )}
      </div>
    </Link>
  );

  const creditPlanLinkMobile = !userLoading && user && (
    <Link href="/pricing" data-disable-progress={true}>
      <div className="flex items-center gap-1.5 px-3 py-2 font-medium rounded-full border border-text-primary/[.07] bg-black/20 backdrop-blur-xl text-text-primary cursor-pointer transition-all hover:bg-primary/[.15] hover:backdrop-blur-none">
        <Image src={creditIcon} alt="Credit" width={14} height={14} />
        <span className="text-sm">{formatNumberWithCommas(userCredits)}</span>
      </div>
    </Link>
  );

  return (
    <header
      className={classNames(
        "w-full transition-all duration-300",
        "h-[56px] sm:h-[60px] px-4 sm:px-5 md:px-8 lg:px-10 xl:px-11",
        {
          [styles.blur]: true,
        }
      )}
    >
      <div className="h-full flex items-center justify-between">
        {/* Logo 区域 */}
        <Link href="/" className={`flex items-center ${styles.logo}`}>
          <TextLogoIcon
            className="text-text-primary w-[91px] sm:hidden"
            alt="VidMuse"
          />
          <LogoIcon
            className="text-text-primary w-[128px] hidden sm:block"
            alt="VidMuse"
          />
        </Link>

        {/* 右侧功能区 */}
        <div className="flex items-center gap-2 sm:gap-5">
          {/* 社交按钮 */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Popover
              trigger={["click", "hover"]}
              placement="bottom"
              arrow={false}
              classNames={{
                root: "!pt-2",
                body: classNames(
                  "!p-6 !pb-[22px] flex bg-background-card! !rounded-xl !w-[198px]",
                  styles.popoverBody
                ),
              }}
              content={
                <>
                  <div className="text-text-primary text-sm font-normal rounded-none flex flex-col items-center">
                    <Image
                      src="/wechat-qrcode.png"
                      alt="Wechat QR Code"
                      width={150}
                      height={150}
                      className="w-full h-auto"
                    />
                    <p className="mt-3 text-center">
                      Welcome to join our WeChat community
                    </p>
                  </div>
                </>
              }
            >
              <Button
                type="plain"
                className="flex items-center justify-center border border-solid border-text-primary/[.07] rounded-full bg-black/20 backdrop-blur-xl transition-all hover:bg-primary/[.15] hover:backdrop-blur-none !cursor-default w-8 !h-8 sm:w-10 sm:!h-10"
                icon={
                  <WechatIcon className="text-text-primary! w-4 sm:w-[18px]" />
                }
                aria-label="Wechat"
              ></Button>
            </Popover>

            <Link
              href={DISCORD_INVITE_URL}
              target="_blank"
              aria-label="Discord"
              className="text-text-primary!"
            >
              <div className="flex items-center justify-center border border-solid border-text-primary/[.07] rounded-full bg-black/20 backdrop-blur-xl transition-all hover:bg-primary/[.15] hover:backdrop-blur-none w-8 !h-8 sm:w-10 sm:!h-10">
                <DiscordIcon className="flex-shrink-0 w-4 sm:w-[18px]" />
              </div>
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-[10px]">
            {user ? (
              <>
                {/* 移动端简化版 Credits */}
                <div className="sm:hidden">{creditPlanLinkMobile}</div>
                {/* 桌面端完整版 Credits */}
                <div className="hidden sm:block">{creditPlanLink}</div>

                <Popover
                  key="user-popover"
                  trigger={["click"]}
                  placement="bottom"
                  arrow={false}
                  classNames={{
                    root: "!pr-0 sm:!pr-8 !pt-2",
                    body: classNames(
                      "!p-2 flex bg-background-card! text-xs text-center !rounded-xl",
                      styles.popoverBody
                    ),
                  }}
                  zIndex={100}
                  content={
                    <>
                      <div className="text-text-primary font-normal p-3 rounded-lg bg-background-elevated w-[240px] sm:w-[260px]">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col items-start w-[180px] sm:w-[230px]">
                            <strong className="text-sm font-medium truncate block">
                              {user.nickname}
                            </strong>

                            <p className="text-xs text-graphite truncate block">
                              {user.email}
                            </p>
                          </div>
                        </div>

                        <div
                          className="w-full h-px mt-3 mb-2 opacity-40"
                          style={{
                            backgroundImage: "url(/dash-line.svg)",
                            backgroundRepeat: "repeat-x",
                          }}
                        ></div>

                        <div className="w-full flex items-center justify-between">
                          <div className="flex flex-col items-start">
                            <p className="text-sm font-medium">
                              {userPlan?.memberShipName}
                            </p>

                            {userPlan?.expireTime && (
                              <p className="text-xs text-graphite">
                                Valid until:{" "}
                                {new Date(
                                  userPlan.expireTime
                                ).toLocaleDateString()}
                              </p>
                            )}
                          </div>

                          <Button
                            type="primary"
                            className="!h-7 !px-3 !text-xs"
                            onClick={onUpgradeClick}
                          >
                            Upgrade
                          </Button>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 mt-2">
                        <Button
                          type="plain"
                          className="w-full justify-start h-10 rounded-lg pl-4 text-text-primary hover:bg-background-card-hover"
                          icon={<ManageIcon className="w-6 h-6" />}
                          onClick={onManageSubscriptionClick}
                        >
                          Manage Subscription
                        </Button>
                        <Link
                          href={feedbackUrl}
                          target="_blank"
                          className="w-full h-10"
                        >
                          <Button
                            type="plain"
                            className="w-full h-full justify-start rounded-lg pl-4 text-text-primary hover:bg-background-card-hover"
                            icon={<ProblemFeedbackIcon className="w-6 h-6" />}
                          >
                            Problem feedback
                          </Button>
                        </Link>
                        <Button
                          type="plain"
                          className="w-full justify-start h-10 rounded-lg pl-4 text-text-primary hover:bg-background-card-hover"
                          icon={<LogoutIcon className="w-6 h-6" />}
                          onClick={() => {
                            window.location.href = "/auth/logout";
                          }}
                        >
                          Logout
                        </Button>
                      </div>
                    </>
                  }
                >
                  <Avatar
                    src={user.avatarUrl || defaultAvatar.src}
                    className="rounded-full cursor-pointer !w-9 !h-9 sm:!w-10 sm:!h-10"
                  />
                </Popover>
              </>
            ) : (
              <>
                {/* 桌面端显示 Pricing 链接 */}
                <div className="hidden sm:block">{creditPlanLink}</div>
                <Link href="/login">
                  <Button
                    className="!h-[40px] !px-4 sm:!px-6 !text-sm"
                    type="primary"
                  >
                    Sign In
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
